import { useMutation, useQueryClient } from "@tanstack/react-query";
import { pathPickMutationApi } from "@/features/pathpick/api/mutation";
import { PathPickOrderRequestBody } from "@/features/pathpick/types/request-body";
import useCompanyIdentity from "@/shared/hooks/useCompanyIdentity";
import useAppToasts from "@/features/line-sheet-sets/hooks/use-app-toasts";
import { refreshPathPickProducts } from "@/features/pathpick/utils/query-cache";

// 서버가 주문 접수(POST 응답) 후 is_buyer_ordered 플래그를 비동기로 반영해,
// 즉시 재조회 응답에는 옛 값이 내려올 수 있다. 반영분을 다시 가져오기 위한 지연 재조회 간격.
const DELAYED_REFETCH_MS = 3000;

// 패스픽 주문 생성 mutation — 담은 상품 선택 주문과 상품 찾기 단건 주문이 공용으로 쓴다.
// 성공 안내 UI(토스트, 모달 닫기 등)는 호출부가 담당한다.
const useCreatePathPickOrder = () => {
  const { companyId } = useCompanyIdentity();
  const queryClient = useQueryClient();
  const { error } = useAppToasts({ id: "GLOBAL" });

  return useMutation({
    mutationFn: (body: PathPickOrderRequestBody) =>
      pathPickMutationApi.createPathPickOrder(companyId as number, body),
    onSuccess: () => {
      const invalidate = () => {
        // isBuyerOrdered 반영 — 주문한 상품이 "작업 전 상품" 탭에서 빠지고
        // "주문한 상품" 탭으로 옮겨간다. 화면에 없는 상품 찾기 목록은 비워,
        // 돌아왔을 때 주문 배지가 뒤늦게 붙지 않게 한다.
        refreshPathPickProducts(queryClient, companyId);
        // 전체 선택 대상(ids_only)과 "전체 n개 선택"의 n도 같은 변화를 따라간다.
        queryClient.invalidateQueries({
          queryKey: ["pathpick", "api-product-ids", companyId],
        });
        // GNB 카운트 갱신
        queryClient.invalidateQueries({
          queryKey: ["pathpick", "counts", companyId],
        });
        // 담은 상품 탭 배지 카운트 — 주문한 상품이 작업 전에서 주문함으로 옮겨간다.
        queryClient.invalidateQueries({
          queryKey: ["pathpick", "basket", "counts", companyId],
        });
      };
      invalidate();
      // 지연 재조회 — queryClient는 전역이라 컴포넌트 언마운트 후 실행돼도 안전하다.
      setTimeout(invalidate, DELAYED_REFETCH_MS);
    },
    onError: (err) => {
      error(err);
    },
  });
};

export default useCreatePathPickOrder;
